import { useEffect, useRef } from 'react'
import { useSocket } from 'providers'
import { useStateValue } from 'providers'
import { useWorld } from './use-world'
import { getActiveSave } from '../components/character-select'
import { getDungeonChat } from 'actions/api'

const MOVE_INTERVAL_MS = 50 // 20fps position broadcast

export const useGameSocket = (dungeonId) => {
  const socket = useSocket()
  const [{ session }] = useStateValue()
  const { spritesRef, currentLevel, updateRemotePlayer, removeRemotePlayer, addDroppedItem, pickupDroppedItem, addChatMessage } = useWorld()
  const currentLevelRef = useRef(currentLevel)
  useEffect(() => { currentLevelRef.current = currentLevel }, [currentLevel])

  useEffect(() => {
    if (!socket || !dungeonId) return

    const username = getActiveSave()?.character?.name || session?.common_name || session?.username || 'Adventurer'
    const playerPos = spritesRef.current?.player?.position
    const x = playerPos?.x ?? 0
    const y = playerPos?.y ?? 0
    const sprite = playerPos?.config?.sprite ?? null
    const level = currentLevelRef.current ?? 0

    socket.emit('dungeon:join', { dungeonId, username, x, y, sprite, level })

    // Receive snapshot of players already in the room
    const onPeers = (peers) => {
      peers.forEach(({ id, username: un, x: px, y: py, sprite: sp, level: lv }) => {
        updateRemotePlayer(id, { username: un, x: px, y: py, sprite: sp ?? null, level: lv ?? 0 })
      })
    }

    const onPlayerJoined = ({ id, username: un, x: px, y: py, sprite: sp, level: lv }) => {
      updateRemotePlayer(id, { username: un, x: px, y: py, sprite: sp ?? null, level: lv ?? 0 })
    }

    const onPlayerMoved = ({ id, x: px, y: py, direction, level: lv }) => {
      updateRemotePlayer(id, { x: px, y: py, direction, level: lv ?? 0 })
    }

    const onPlayerLeft = ({ id }) => {
      removeRemotePlayer(id)
    }

    const onItemDrops = (items) => items.forEach((item) => addDroppedItem(item))
    const onItemDropped = (item) => addDroppedItem(item)
    const onItemPickedUp = ({ id }) => pickupDroppedItem(id)
    const onChat = (msg) => addChatMessage(msg)

    // Load chat history
    getDungeonChat(dungeonId).then((data) => {
      (data?.messages ?? []).forEach((msg) => addChatMessage(msg))
    }).catch(() => {})

    socket.on('dungeon:peers', onPeers)
    socket.on('dungeon:player_joined', onPlayerJoined)
    socket.on('dungeon:player_moved', onPlayerMoved)
    socket.on('dungeon:player_left', onPlayerLeft)
    socket.on('dungeon:item_drops', onItemDrops)
    socket.on('dungeon:item_dropped', onItemDropped)
    socket.on('dungeon:item_picked_up', onItemPickedUp)
    socket.on('dungeon:chat', onChat)

    // Broadcast own position at fixed interval (reads from refs — no stale closures)
    const interval = setInterval(() => {
      const pos = spritesRef.current?.player?.position
      if (pos) {
        socket.emit('dungeon:move', {
          dungeonId,
          x: pos.x,
          y: pos.y,
          direction: pos.direction,
          level: currentLevelRef.current ?? 0,
        })
      }
    }, MOVE_INTERVAL_MS)

    return () => {
      clearInterval(interval)
      socket.emit('dungeon:leave', { dungeonId })
      socket.off('dungeon:peers', onPeers)
      socket.off('dungeon:player_joined', onPlayerJoined)
      socket.off('dungeon:player_moved', onPlayerMoved)
      socket.off('dungeon:player_left', onPlayerLeft)
      socket.off('dungeon:item_drops', onItemDrops)
      socket.off('dungeon:item_dropped', onItemDropped)
      socket.off('dungeon:item_picked_up', onItemPickedUp)
    }
  }, [socket, dungeonId])
}
